import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import styles from './MaintenancePage.module.css';
import { useLang } from '../../context/LanguageContext';

/* ── Icons ────────────────────────────────────────────── */
const IconFeature = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
  </svg>
);
const IconBug = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M8 2l1.5 1.5M15.5 3.5L17 2M12 2a4 4 0 0 0-4 4v1H4l2 3H4v3h2l-2 3h4v1a4 4 0 0 0 8 0v-1h4l-2-3h2v-3h-2l2-3h-4V6a4 4 0 0 0-4-4z"/>
  </svg>
);
const IconPerf = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);
const IconUI = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
  </svg>
);
const IconMigration = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="17 1 21 5 17 9"/><path d="M3 11V9a4 4 0 0 1 4-4h14"/><polyline points="7 23 3 19 7 15"/><path d="M21 13v2a4 4 0 0 1-4 4H3"/>
  </svg>
);
const IconAPI = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/><line x1="12" y1="2" x2="12" y2="22"/>
  </svg>
);
const IconSecurity = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);
const IconSupport = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
  </svg>
);

/* ── Data ──────────────────────────────────────────────── */
const deliverables = [
  { icon: <IconFeature />,   label: { fr: 'Ajout de nouvelles fonctionnalités', en: 'New feature development'      }, desc: { fr: 'Étendre votre logiciel sans déstabiliser ce qui fonctionne déjà.', en: 'Extend your software without destabilizing what already works.'     } },
  { icon: <IconBug />,       label: { fr: 'Correction de bugs & stabilisation',  en: 'Bug fixes & stabilization'     }, desc: { fr: 'Identifier, reproduire et corriger les problèmes en profondeur.',  en: 'Identify, reproduce and fix issues thoroughly.'                     } },
  { icon: <IconPerf />,      label: { fr: 'Amélioration des performances',       en: 'Performance improvements'      }, desc: { fr: 'Temps de chargement, requêtes DB, cache — des gains mesurables.', en: 'Load times, DB queries, cache — measurable gains.'                   } },
  { icon: <IconUI />,        label: { fr: "Refonte d'interface (UI/UX)",         en: 'UI/UX redesign'                 }, desc: { fr: 'Moderniser le design sans toucher au backend ni aux données.',    en: 'Modernize the design without touching the backend or data.'          } },
  { icon: <IconMigration />, label: { fr: 'Migration technologique',             en: 'Technology migration'           }, desc: { fr: 'Passer de PHP à Next.js, ou moderniser une stack obsolète.',      en: 'Moving from PHP to Next.js, or modernizing an outdated stack.'      } },
  { icon: <IconAPI />,       label: { fr: "Intégration d'APIs tierces",          en: 'Third-party API integration'   }, desc: { fr: 'Connecter votre outil à des services externes (paiement, CRM, IA…).', en: 'Connect your tool to external services (payment, CRM, AI…).'     } },
  { icon: <IconSecurity />,  label: { fr: 'Amélioration de la sécurité',        en: 'Security improvements'         }, desc: { fr: 'Audit des vulnérabilités et corrections avant que les problèmes arrivent.', en: 'Vulnerability audit and fixes before problems occur.'            } },
  { icon: <IconSupport />,   label: { fr: 'Accompagnement technique ponctuel',   en: 'On-demand technical support'   }, desc: { fr: 'Répondre à une urgence ou débloquer une situation complexe.',      en: 'Respond to an emergency or unblock a complex situation.'            } },
];

const situations = [
  {
    num: '01',
    title: { fr: "Votre développeur initial n'est plus disponible",          en: "Your original developer is no longer available"             },
    desc:  { fr: "Le code est là, mais personne ne le maîtrise vraiment. Je prends en main n'importe quel projet existant sérieux.", en: "The code is there, but nobody really masters it. I take over any serious existing project." },
  },
  {
    num: '02',
    title: { fr: 'Votre app est lente, instable ou difficile à faire évoluer', en: 'Your app is slow, unstable or hard to evolve'                },
    desc:  { fr: "Une dette technique qui s'accumule finit par bloquer toute évolution. Je l'identifie et je la traite méthodiquement.", en: "Accumulated technical debt eventually blocks all evolution. I identify it and address it methodically." },
  },
  {
    num: '03',
    title: { fr: "Des demandes qui s'accumulent sans être traitées",          en: 'A backlog piling up without being addressed'                 },
    desc:  { fr: "Vos utilisateurs ou vos équipes attendent des améliorations depuis trop longtemps. Je remets le flux en mouvement.", en: "Your users or teams have been waiting for improvements for too long. I get the pipeline moving again." },
  },
];

const differentiators = [
  { title: { fr: "Je reprends n'importe quel stack sérieux", en: 'I work with any serious stack'                  }, desc: { fr: "React, Vue, Laravel, Django, WordPress, Node — peu importe la technologie, je m'adapte à votre contexte existant.",                              en: "React, Vue, Laravel, Django, WordPress, Node — whatever the tech, I adapt to your existing context."                     } },
  { title: { fr: 'Pas besoin de tout réécrire pour avancer',  en: 'No need to rewrite everything to move forward' }, desc: { fr: "La réécriture complète est rarement la bonne réponse. Je cherche d'abord à faire évoluer ce qui existe de manière intelligente.", en: "A full rewrite is rarely the right answer. I first look to evolve what exists in an intelligent way."                         } },
  { title: { fr: "Visibilité constante sur l'avancement",     en: 'Constant visibility on progress'               }, desc: { fr: "Des livraisons régulières et une communication directe — vous savez toujours où en est le projet.",                               en: "Regular deliveries and direct communication — you always know where the project stands."                                    } },
];

const techs = ['Sites WordPress', 'Applications web', 'E-commerce', 'Bases de données', 'Hébergement', 'Performance', 'Sécurité', 'Mises à jour'];

/* ── Animations ─────────────────────────────────────────── */
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.09, delayChildren: 0.05 } } };
const fadeUp  = { hidden: { opacity: 0, y: 28, filter: 'blur(4px)' }, visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } } };

export default function MaintenancePage() {
  const delivRef   = useRef(null);
  const sitRef     = useRef(null);
  const diffRef    = useRef(null);
  const delivInView   = useInView(delivRef,   { once: true, margin: '-60px' });
  const sitInView     = useInView(sitRef,     { once: true, margin: '-60px' });
  const diffInView    = useInView(diffRef,    { once: true, margin: '-60px' });
  const { lang } = useLang();

  const ui = {
    fr: {
      breadcrumb: 'Maintenance & évolution logicielle',
      heroTitle:  'Votre logiciel ou site web ne répond plus',
      heroAccent: 'à vos besoins actuels ?',
      heroSub:    'Je peux l’améliorer, le moderniser ou y ajouter de nouvelles fonctionnalités — sans repartir de zéro.',
      ctaPrimary: 'Envoyer les détails du projet',
      ctaGhost:   'Voir les livrables',
      delivEy:    'Ce que je fais',
      delivT1:    "Chaque type d'intervention,",
      delivT2:    'pris en charge.',
      sitEy:      'Vous vous reconnaissez ?',
      sitT1:      'Trois situations',
      sitT2:      'que je rencontre souvent.',
      diffEy:     'Ce qui me différencie',
      diffT1:     'Reprendre un projet,',
      diffT2:     "c'est aussi un métier.",
      techLabel:  'Stacks supportés',
    },
    en: {
      breadcrumb: 'Maintenance & software evolution',
      heroTitle:  'Your software or website no longer meets',
      heroAccent: 'your current needs?',
      heroSub:    'I can improve it, modernize it or add new features — without starting from scratch.',
      ctaPrimary: 'Send project details',
      ctaGhost:   'See deliverables',
      delivEy:    'What I do',
      delivT1:    'Every type of intervention,',
      delivT2:    'handled.',
      sitEy:      'Do you recognize yourself?',
      sitT1:      'Three situations',
      sitT2:      'I encounter often.',
      diffEy:     'What sets me apart',
      diffT1:     'Taking over a project',
      diffT2:     'is also a craft.',
      techLabel:  'Supported stacks',
    },
  }[lang];

  return (
    <div className={styles.page}>

      {/* ── HERO ── */}
      <section className={styles.hero}>
        <div className={styles.heroBg}>
          <div className={styles.gridLines} />
          <div className={styles.glow} />
        </div>
        <div className={styles.heroInner}>
          <motion.div className={styles.breadcrumb}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
            <Link to="/services" className={styles.breadcrumbLink}>Services</Link>
            <span className={styles.sep}>/</span>
            <span>{ui.breadcrumb}</span>
          </motion.div>

          <motion.h1 className={styles.heroTitle}
            initial={{ opacity: 0, y: 28 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}>
            {ui.heroTitle}{' '}
            <span className={styles.accent}>{ui.heroAccent}</span>
          </motion.h1>

          <motion.p className={styles.heroSub}
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: 'easeOut', delay: 0.24 }}>
            {ui.heroSub}
          </motion.p>

          <motion.div className={styles.heroCtas}
            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: 'easeOut', delay: 0.36 }}>
            <Link to="/contact" className={styles.ctaPrimary}>
              {ui.ctaPrimary}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </Link>
            <a href="#livrables" className={styles.ctaGhost}>{ui.ctaGhost}</a>
          </motion.div>
        </div>
      </section>

      {/* ── LIVRABLES ── */}
      <section id="livrables" className={styles.section} ref={delivRef}>
        <div className={styles.container}>
          <div className={styles.sectionHead} data-aos="fade-up">
            <p className={styles.eyebrow}>{ui.delivEy}</p>
            <h2 className={styles.sectionTitle}>{ui.delivT1}<br />{ui.delivT2}</h2>
          </div>
          <motion.div className={styles.delivGrid} variants={stagger} initial="hidden" animate={delivInView ? 'visible' : 'hidden'}>
            {deliverables.map((d) => (
              <motion.div key={d.label.fr} className={styles.delivCard} variants={fadeUp}>
                <div className={styles.delivIcon}>{d.icon}</div>
                <div>
                  <p className={styles.delivLabel}>{d.label[lang]}</p>
                  <p className={styles.delivDesc}>{d.desc[lang]}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── VOUS VOUS RECONNAISSEZ ── */}
      <section className={`${styles.section} ${styles.sectionAlt}`} ref={sitRef}>
        <div className={styles.container}>
          <div className={styles.sectionHead} data-aos="fade-up">
            <p className={styles.eyebrow}>{ui.sitEy}</p>
            <h2 className={styles.sectionTitle}>{ui.sitT1}<br />{ui.sitT2}</h2>
          </div>
          <motion.div className={styles.sitGrid} variants={stagger} initial="hidden" animate={sitInView ? 'visible' : 'hidden'}>
            {situations.map((s) => (
              <motion.div key={s.num} className={styles.sitCard} variants={fadeUp}>
                <span className={styles.sitNum}>{s.num}</span>
                <h3 className={styles.sitTitle}>{s.title[lang]}</h3>
                <p className={styles.sitDesc}>{s.desc[lang]}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── DIFFÉRENCIATEURS ── */}
      <section className={styles.section} ref={diffRef}>
        <div className={styles.container}>
          <div className={styles.sectionHead} data-aos="fade-up">
            <p className={styles.eyebrow}>{ui.diffEy}</p>
            <h2 className={styles.sectionTitle}>{ui.diffT1}<br />{ui.diffT2}</h2>
          </div>
          <motion.div className={styles.diffList} variants={stagger} initial="hidden" animate={diffInView ? 'visible' : 'hidden'}>
            {differentiators.map((d) => (
              <motion.div key={d.title.fr} className={styles.diffItem} variants={fadeUp}>
                <span className={styles.diffArrow}>→</span>
                <div>
                  <p className={styles.diffTitle}>{d.title[lang]}</p>
                  <p className={styles.diffDesc}>{d.desc[lang]}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── TECHNOLOGIES ── */}
      <section className={`${styles.section} ${styles.sectionAlt}`} data-aos="fade-up">
        <div className={styles.container}>
          <div className={styles.techRow}>
            <p className={styles.techLabel}>{ui.techLabel}</p>
            <div className={styles.techBadges}>
              {techs.map((t) => <span key={t} className={styles.techBadge}>{t}</span>)}
            </div>
          </div>
        </div>
      </section>


    </div>
  );
}
